  import { createRootRoute, createRoute, createRouter, Outlet, useRouterState, useNavigate } from '@tanstack/react-router'
import HomePage from '@pages/HomePage'
import Header from '@components/Header'
import Footer from '@components/Footer'
import ConsentManager from '@components/ConsentManager'
import FileTransferBackground from '@components/FileTransferBackground'
import { parseShareLocation } from './utils/shareLink'
import { useMemo, useEffect, useState, lazy, Suspense } from 'react'
import { useMantineColorScheme } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { toast } from '@services/toast'
import { applyCachedBrandingTheme } from './utils/brandingTheme'

const SbomPageLazy = lazy(() => import('@pages/SbomPage'))
const DownloadPageLazy = lazy(() => import('@pages/DownloadPage'))
const LoginPageLazy = lazy(() => import('@pages/LoginPage'))
const ForgotPasswordPageLazy = lazy(() => import('@pages/ForgotPasswordPage'))
const ResetPasswordPageLazy = lazy(() => import('@pages/ResetPasswordPage'))
const DashboardLayoutLazy = lazy(() => import('@pages/dashboard/DashboardLayout'))
const DashboardUsagePageLazy = lazy(() => import('@pages/dashboard/UsagePage'))
const DashboardLinksPageLazy = lazy(() => import('@pages/dashboard/LinksPage'))
const DashboardLicensingManagePageLazy = lazy(() => import('@pages/dashboard/LicensingManagePage'))
const DashboardLicensingSeatsPageLazy = lazy(() => import('@pages/dashboard/LicensingSeatsPage'))
const DashboardLicensingInvitationsPageLazy = lazy(
  () => import('@pages/dashboard/LicensingInvitationsPage'),
)
const DashboardEnterpriseDomainVerificationPageLazy = lazy(
  () => import('@pages/dashboard/OrganizationDomainVerificationPage'),
)
const DashboardEnterpriseBrandingPageLazy = lazy(
  () => import('@pages/dashboard/OrganizationBrandingPage'),
)
const DashboardEnterpriseSsoPageLazy = lazy(() => import('@pages/dashboard/OrganizationSsoPage'))
const DashboardEnterpriseSubdomainsPageLazy = lazy(() => import('@pages/dashboard/OrganizationSubdomainsPage'))
const DashboardOrganizationMembersPageLazy = lazy(() => import('@pages/dashboard/OrganizationMembersPage'))
const SignupPageLazy = lazy(() => import('@pages/SignupPage'))
const OAuthCallbackPageLazy = lazy(() => import('@pages/OAuthCallbackPage'))
const TermsPageLazy = lazy(() => import('@pages/TermsPage'))
const LegalNoticePageLazy = lazy(() => import('@pages/LegalNoticePage'))
const PrivacyPageLazy = lazy(() => import('@pages/PrivacyPage'))
const CookiesPageLazy = lazy(() => import('@pages/CookiesPage'))
const ContactPageLazy = lazy(() => import('@pages/ContactPage'))
const FAQPageLazy = lazy(() => import('@pages/FAQPage'))
const OAuthTermsPageLazy = lazy(() => import('@pages/OAuthTermsPage'))
const AcceptInvitationPageLazy = lazy(() => import('@pages/accept/AcceptInvitationPage'))
const AnonymousUsagePageLazy = lazy(() => import('@pages/AnonymousUsagePage'))
const BlogListPageLazy = lazy(() => import('@pages/BlogListPage'))
const BlogDetailPageLazy = lazy(() => import('@pages/BlogDetailPage'))
const StatsPageLazy = lazy(() => import('@pages/StatsPage'))
const UploadPageLazy = lazy(() => import('@pages/UploadPage'))
const DashboardSubscriptionPageLazy = lazy(() => import('@pages/dashboard/SubscriptionPage'))
const SubscriptionTransitionPageLazy = lazy(() => import('@pages/dashboard/SubscriptionTransitionPage'))

function ScrollToTopOnRouteChange() {
  const location = useRouterState({
    select: (state) => state.location,
  })

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    }
  }, [location.href])

  return null
}

function SeoHeadManager() {
  const { t, i18n } = useTranslation()

  const location = useRouterState({
    select: (state) => state.location,
  })

  const routeTitleKey = useMemo(() => {
    const path = location.pathname ?? '/'
    if (path === '/') return undefined
    if (path.startsWith('/d/')) return 'seo.title.download'
    if (path.startsWith('/usage')) return 'seo.title.usage'
    if (path.startsWith('/contact')) return 'seo.title.contact'
    if (path.startsWith('/login')) return 'seo.title.login'
    if (path.startsWith('/signup')) return 'seo.title.signup'
    if (path.startsWith('/dashboard')) return 'seo.title.user'
    if (path.startsWith('/terms')) return 'seo.title.terms'
    if (path.startsWith('/legal-notice')) return 'seo.title.legal'
    if (path.startsWith('/privacy')) return 'seo.title.privacy'
    if (path.startsWith('/cookies')) return 'seo.title.cookies'
    if (path.startsWith('/faq')) return 'seo.title.faq'
    if (path.startsWith('/sbom')) return 'seo.title.sbom'
    if (path.startsWith('/blog')) return 'seo.title.blog'
    return undefined
  }, [location.pathname])

  const descriptionKey = useMemo(() => {
    const path = location.pathname ?? '/'
    if (path === '/') return 'seo.description.home'
    if (path.startsWith('/d/')) return 'seo.description.download'
    if (path.startsWith('/usage')) return 'seo.description.usage'
    if (path.startsWith('/contact')) return 'seo.description.contact'
    if (path.startsWith('/login')) return 'seo.description.login'
    if (path.startsWith('/signup')) return 'seo.description.signup'
    if (path.startsWith('/dashboard')) return 'seo.description.user'
    if (path.startsWith('/terms')) return 'seo.description.terms'
    if (path.startsWith('/legal-notice')) return 'seo.description.legal'
    if (path.startsWith('/privacy')) return 'seo.description.privacy'
    if (path.startsWith('/cookies')) return 'seo.description.cookies'
    if (path.startsWith('/faq')) return 'seo.description.faq'
    if (path.startsWith('/sbom')) return 'seo.description.sbom'
    if (path.startsWith('/blog')) return 'seo.description.blog'
    return undefined
  }, [location.pathname])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const baseTitle = t(
      'app.title',
      'SendAllFiles - Encrypted zero-knowledge file sharing and secure transfer',
    )
    const fullTitle = routeTitleKey !== undefined ? `${t(routeTitleKey)} | ${baseTitle}` : baseTitle
    document.title = fullTitle

    const language = (i18n.resolvedLanguage || i18n.language || 'en').replace('_', '-')
    document.documentElement.lang = language
    const fallbackDescription =
      'SendAllFiles is a zero-knowledge, end-to-end encrypted file sharing service. Share large files securely with client-side encryption, password-protected links, and strict download limits.'
    const description = descriptionKey ? t(descriptionKey) : fallbackDescription
    const setMeta = (selector: string, content: string) => {
      document.querySelector(selector)?.setAttribute('content', content)
    }

    setMeta('meta[name="description"]', description)
    setMeta('meta[property="og:title"]', fullTitle)
    setMeta('meta[property="og:description"]', description)
    setMeta('meta[property="og:type"]', 'website')
    setMeta('meta[property="og:locale"]', language.replace('-', '_'))
    setMeta('meta[name="twitter:title"]', fullTitle)
    setMeta('meta[name="twitter:description"]', description)

    // Per-page canonical. The static <link rel="canonical"> is hard-pinned to "/", which makes
    // every page look like a duplicate of the homepage to search engines. Point it at this page
    // instead (path only - drop ?lng / hash - and always the canonical apex domain).
    let canonicalHref = `https://sendallfiles.com${location.pathname}`
    if (location.pathname === '/blog' && typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href)
      const page = Number.parseInt(currentUrl.searchParams.get('page') || '', 10)
      if (!currentUrl.searchParams.get('q') && Number.isFinite(page) && page > 1) {
        canonicalHref += `?page=${page}`
      }
    }
    let canonical = document.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', canonicalHref)
    setMeta('meta[property="og:url"]', canonicalHref)
  }, [routeTitleKey, descriptionKey, t, i18n.language, i18n.resolvedLanguage, location.href, location.pathname])

  return null
}

function IndexRouteComponent() {
  return <HomePage />
}

function RootLayout() {
  const { colorScheme } = useMantineColorScheme()
  const [showBackground, setShowBackground] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return
    }

    const trigger = () => {
      setTimeout(() => {
        setShowBackground(true)
      }, 200)
    }

    if (document.readyState === 'complete') {
      trigger()
    } else {
      window.addEventListener(
        'load',
        () => {
          trigger()
        },
        { once: true },
      )
    }
  }, [])
  return (
    <>
      <ScrollToTopOnRouteChange />
      <SeoHeadManager />
      {showBackground && (
        <FileTransferBackground mode={colorScheme === 'dark' ? 'dark' : 'light'} />
      )}
      <Header />
      <main id="main-content">
        <Outlet />
      </main>
      <Footer />
      <ConsentManager />
    </>
  )
}

const rootRoute = createRootRoute({
  component: RootLayout
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: IndexRouteComponent
})

const downloadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/d/$uploadId',
  component: function DownloadWrapper() {
    const { uploadId } = downloadRoute.useParams()
    const fromUrl = useMemo(() => parseShareLocation(window.location).secret, [])
    const navigate = useNavigate()
    const { t } = useTranslation()

    // Persist secret in sessionStorage keyed by uploadId; read it back if fragment missing
    const resolvedSecret = useMemo(() => {
      const key = `saf:secret:${uploadId}`
      if (fromUrl && fromUrl.length > 0) {
        try { sessionStorage.setItem(key, fromUrl) } catch { /* ignore */ }
        return fromUrl
      }
      try { return sessionStorage.getItem(key) ?? undefined } catch { return undefined }
    }, [fromUrl, uploadId])

    // Hide the secret from the URL fragment as soon as we parse it
    useEffect(() => {
      if (window.location.hash) {
        const url = new URL(window.location.href)
        url.hash = ''
        window.history.replaceState(null, '', url.toString())
      }
    }, [])

    // If we cannot resolve a secret, show a toast error and redirect home
    useEffect(() => {
      if (!resolvedSecret) {
        const message = t('download.invalidLink', 'Something is wrong with this download link.')
        toast.error(message)
        navigate({ to: '/', replace: true })
      }
    }, [resolvedSecret, navigate, t])

    if (!resolvedSecret) {
      return null
    }
    return (
      <Suspense fallback={null}>
        <DownloadPageLazy uploadId={uploadId} secret={resolvedSecret} />
      </Suspense>
    )
  }
})

const sbomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sbom',
  component: function SbomRouteComponent() {
    return (
      <Suspense fallback={null}>
        <SbomPageLazy />
      </Suspense>
    )
  },
})

const blogListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/blog',
  component: function BlogListRouteComponent() {
    return (
      <Suspense fallback={null}>
        <BlogListPageLazy />
      </Suspense>
    )
  },
})

const blogDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/blog/$slug',
  component: function BlogDetailRouteComponent() {
    const { slug } = blogDetailRoute.useParams()
    return (
      <Suspense fallback={null}>
        <BlogDetailPageLazy slug={slug} />
      </Suspense>
    )
  },
})

const contactRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contact',
  component: function ContactRouteComponent() {
    return (
      <Suspense fallback={null}>
        <ContactPageLazy />
      </Suspense>
    )
  },
})

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: function TermsRouteComponent() {
    return (
      <Suspense fallback={null}>
        <TermsPageLazy />
      </Suspense>
    )
  },
})

const legalNoticeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal-notice',
  component: function LegalNoticeRouteComponent() {
    return (
      <Suspense fallback={null}>
        <LegalNoticePageLazy />
      </Suspense>
    )
  },
})

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: function PrivacyRouteComponent() {
    return (
      <Suspense fallback={null}>
        <PrivacyPageLazy />
      </Suspense>
    )
  },
})

const cookiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cookies',
  component: function CookiesRouteComponent() {
    return (
      <Suspense fallback={null}>
        <CookiesPageLazy />
      </Suspense>
    )
  },
})

const faqRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/faq',
  component: function FAQRouteComponent() {
    return (
      <Suspense fallback={null}>
        <FAQPageLazy />
      </Suspense>
    )
  },
})

const anonymousUsageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/usage',
  component: function AnonymousUsageRouteComponent() {
    return (
      <Suspense fallback={null}>
        <AnonymousUsagePageLazy />
      </Suspense>
    )
  },
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: function LoginRouteComponent() {
    return (
      <Suspense fallback={null}>
        <LoginPageLazy />
      </Suspense>
    )
  },
})

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  validateSearch: (search: Record<string, unknown>): { returnUrl?: string } => {
    const returnUrl = typeof search.returnUrl === 'string' ? search.returnUrl : undefined;
    return returnUrl ? { returnUrl } : {};
  },
  component: function SignupRouteComponent() {
    return (
      <Suspense fallback={null}>
        <SignupPageLazy />
      </Suspense>
    )
  },
})

const forgotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: function ForgotPasswordRouteComponent() {
    return (
      <Suspense fallback={null}>
        <ForgotPasswordPageLazy />
      </Suspense>
    )
  },
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: function ResetPasswordRouteComponent() {
    return (
      <Suspense fallback={null}>
        <ResetPasswordPageLazy />
      </Suspense>
    )
  },
})

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: function DashboardRouteComponent() {
    return (
      <Suspense fallback={null}>
        <DashboardLayoutLazy />
      </Suspense>
    )
  },
})

const dashboardIndexRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/',
  beforeLoad: ({ navigate }) => {
    navigate({ to: '/dashboard/usage', replace: true })
  },
})

const dashboardLinksRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/links',
  component: function DashboardLinksRouteComponent() {
    return (
      <Suspense fallback={null}>
        <DashboardLinksPageLazy />
      </Suspense>
    )
  },
})

const dashboardUsageRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/usage',
  component: function DashboardUsageRouteComponent() {
    return (
      <Suspense fallback={null}>
        <DashboardUsagePageLazy />
      </Suspense>
    )
  },
})

const dashboardLicensingManageRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/licensing/manage',
  component: function DashboardLicensingManageRouteComponent() {
    return (
      <Suspense fallback={null}>
        <DashboardLicensingManagePageLazy />
      </Suspense>
    )
  },
})

const dashboardLicensingSeatsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/licensing/seats',
  component: function DashboardLicensingSeatsRouteComponent() {
    return (
      <Suspense fallback={null}>
        <DashboardLicensingSeatsPageLazy />
      </Suspense>
    )
  },
})

const dashboardLicensingInvitationsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/licensing/invitations',
  component: function DashboardLicensingInvitationsRouteComponent() {
    return (
      <Suspense fallback={null}>
        <DashboardLicensingInvitationsPageLazy />
      </Suspense>
    )
  },
})

const dashboardEnterpriseDomainVerificationRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/organization/domain-verification',
  component: function DashboardEnterpriseDomainVerificationRouteComponent() {
    return (
      <Suspense fallback={null}>
        <DashboardEnterpriseDomainVerificationPageLazy />
      </Suspense>
    )
  },
})

const dashboardEnterpriseBrandingRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/organization/branding',
  component: function DashboardEnterpriseBrandingRouteComponent() {
    return (
      <Suspense fallback={null}>
        <DashboardEnterpriseBrandingPageLazy />
      </Suspense>
    )
  },
})

const dashboardEnterpriseSsoRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/organization/sso',
  component: function DashboardEnterpriseSsoRouteComponent() {
    return (
      <Suspense fallback={null}>
        <DashboardEnterpriseSsoPageLazy />
      </Suspense>
    )
  },
})

const dashboardEnterpriseSubdomainsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/organization/subdomains',
  component: function DashboardEnterpriseSubdomainsRouteComponent() {
    return (
      <Suspense fallback={null}>
        <DashboardEnterpriseSubdomainsPageLazy />
      </Suspense>
    )
  },
})

const dashboardOrganizationMembersRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/organization/members',
  component: function DashboardOrganizationMembersRouteComponent() {
    return (
      <Suspense fallback={null}>
        <DashboardOrganizationMembersPageLazy />
      </Suspense>
    )
  },
})

const dashboardSubscriptionRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/subscription',
  validateSearch: (search: Record<string, unknown>): { cadence?: 'monthly' | 'yearly' } => {
    const cadence = search.cadence === 'monthly' || search.cadence === 'yearly'
      ? search.cadence
      : undefined;
    return cadence ? { cadence } : {};
  },
  component: function DashboardSubscriptionRouteComponent() {
    return (
      <Suspense fallback={null}>
        <DashboardSubscriptionPageLazy />
      </Suspense>
    )
  },
})


const subscriptionCancelSuccessRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/subscription/cancel-success',
  component: function SubscriptionCancelSuccessWrapper() {
    return (
      <Suspense fallback={null}>
        <SubscriptionTransitionPageLazy />
      </Suspense>
    );
  },
})

const subscriptionTransitionRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/subscription/transition',
  component: function SubscriptionTransitionWrapper() {
    return (
      <Suspense fallback={null}>
        <SubscriptionTransitionPageLazy reportNewSubscriptionConversion />
      </Suspense>
    );
  },
})

const subscriptionUpdateSuccessRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/subscription/update-success',
  component: function SubscriptionUpdateSuccessWrapper() {
    return (
      <Suspense fallback={null}>
        <SubscriptionTransitionPageLazy />
      </Suspense>
    );
  },
})

const oauthCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth-callback',
  component: function OAuthCallbackRouteComponent() {
    return (
      <Suspense fallback={null}>
        <OAuthCallbackPageLazy />
      </Suspense>
    )
  },
})

const oauthTermsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth-terms',
  component: function OAuthTermsRouteComponent() {
    return (
      <Suspense fallback={null}>
        <OAuthTermsPageLazy />
      </Suspense>
    )
  },
})

const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/license/invitations/accept',
  component: function AcceptInvitationRouteComponent() {
    return (
      <Suspense fallback={null}>
        <AcceptInvitationPageLazy />
      </Suspense>
    )
  },
})

const statsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/__stats',
  component: function StatsRouteComponent() {
    return (
      <Suspense fallback={null}>
        <StatsPageLazy />
      </Suspense>
    )
  },
})

const uploadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/upload',
  component: function UploadRouteComponent() {
    return (
      <Suspense fallback={null}>
        <UploadPageLazy />
      </Suspense>
    )
  },
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  downloadRoute,
  sbomRoute,
  blogListRoute,
  blogDetailRoute,
  anonymousUsageRoute,
  contactRoute,
  termsRoute,
  legalNoticeRoute,
  privacyRoute,
  cookiesRoute,
  faqRoute,
  loginRoute,
  signupRoute,
  forgotRoute,
  resetPasswordRoute,
  dashboardRoute.addChildren([
    dashboardIndexRoute,
    dashboardUsageRoute,
    dashboardLinksRoute,
    dashboardSubscriptionRoute,
    subscriptionCancelSuccessRoute,
    subscriptionTransitionRoute,
    subscriptionUpdateSuccessRoute,
    dashboardLicensingManageRoute,
    dashboardLicensingSeatsRoute,
    dashboardLicensingInvitationsRoute,
    dashboardEnterpriseDomainVerificationRoute,
    dashboardEnterpriseBrandingRoute,
    dashboardEnterpriseSsoRoute,
    dashboardEnterpriseSubdomainsRoute,
    dashboardOrganizationMembersRoute,
  ]),
  oauthCallbackRoute,
  acceptInvitationRoute,
  oauthTermsRoute,
  statsRoute,
  uploadRoute,
])

export const router = createRouter({
  routeTree,
  context: {
    applyCachedBrandingTheme,
  },
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
